<script setup>
import LoadingAnimation from '../LoadingAnimation.vue'

const props = defineProps({
  word: {
    type: String,
    required: true,
  },
  explanation: {
    type: String,
    default: '',
  },
  explanationError: {
    type: String,
    default: '',
  },
  isExplanationLoading: {
    type: Boolean,
    default: false,
  },
  isConfirming: {
    type: Boolean,
    default: false,
  },
  isSaving: {
    type: Boolean,
    default: false,
  },
  isSaved: {
    type: Boolean,
    default: false,
  },
  canAddKnownWords: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits([
  'request-add-known-word',
  'confirm-add-known-word',
  'cancel-add-known-word',
])

const unknownWordButtonLabel = () => {
  if (props.isSaved) return 'Known'
  if (props.isSaving) return 'Saving...'

  return 'Add to known'
}
</script>

<template>
  <div class="explanation">
    <div class="explanation-text">
      <span class="word">{{ word }}</span>
      <LoadingAnimation
        v-if="isExplanationLoading"
        class="definition"
        label="Loading explanation"
      />
      <span v-else-if="explanationError" class="definition error">
        {{ explanationError }}
      </span>
      <span v-else class="definition">{{ explanation }}</span>
    </div>
    <div class="unknown-word-actions">
      <template v-if="isConfirming">
        <button
          class="unknown-word-button confirm"
          type="button"
          :disabled="isSaving"
          @click="emit('confirm-add-known-word', word)"
        >
          Confirm
        </button>
        <button
          class="unknown-word-button cancel"
          type="button"
          :disabled="isSaving"
          @click="emit('cancel-add-known-word', word)"
        >
          Cancel
        </button>
      </template>
      <button
        v-else
        class="unknown-word-button"
        type="button"
        :disabled="!canAddKnownWords || isSaving || isSaved"
        @click="emit('request-add-known-word', word)"
      >
        {{ unknownWordButtonLabel() }}
      </button>
    </div>
  </div>
</template>

<style scoped>
.explanation {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 8px 12px;
  margin: 4px 0;
  background-color: rgba(0, 0, 0, 0.03);
  border-radius: 4px;
}

.explanation-text {
  min-width: 0;
}

.word {
  font-weight: 600;
  color: #2c3e50;
  margin-right: 8px;
}

.definition {
  color: #34495e;
}

.definition.error {
  color: #8b1e1e;
  font-weight: 700;
}

.unknown-word-actions {
  display: flex;
  flex-shrink: 0;
  flex-wrap: wrap;
  gap: 0.4rem;
  justify-content: flex-end;
}

.unknown-word-button {
  flex-shrink: 0;
  border: 1px solid rgba(76, 175, 80, 0.34);
  background: rgba(76, 175, 80, 0.1);
  color: #1f6f2d;
  border-radius: 8px;
  min-height: 2rem;
  padding: 0.35rem 0.65rem;
  cursor: pointer;
  font-weight: 700;
}

.unknown-word-button:hover:not(:disabled) {
  border-color: #4caf50;
  background: rgba(76, 175, 80, 0.18);
}

.unknown-word-button:disabled {
  cursor: not-allowed;
  opacity: 0.64;
}

.unknown-word-button.cancel {
  border-color: rgba(111, 120, 110, 0.3);
  background: rgba(111, 120, 110, 0.1);
  color: #4b554a;
}

@media (max-width: 640px) {
  .explanation {
    align-items: stretch;
    flex-direction: column;
  }

  .unknown-word-actions,
  .unknown-word-button {
    width: 100%;
  }
}
</style>
